// frontend/geopolitikia/src/components/MobileData.jsx
import React from 'react';
import {
  Crown,
  HandCoins,
  Shield,
  Laptop,
  Users,
  Handshake,
  Leaf,
  Scale,
  Radiation,
} from "lucide-react";
import classNames from "classnames";

const MobileData = ({ gameState, impactsByParam, redBtn, nucleaire, nuclearValue, hasImpactNucl, impactNucl, handleInvest, pendingInvestments, canPerformAction }) => {

  const MobileStatCard = ({ label, value, impact, icon, color, onInvest, isSelected, isInvestDisabled }) => {
    const isClickable = typeof onInvest === 'function' && !isInvestDisabled;
    const cardClasses = classNames(
      "bg-slate-700/50 rounded-lg p-3 border border-slate-600",
      {
        "cursor-pointer hover:scale-105 transition-transform duration-300": isClickable,
        "cursor-not-allowed opacity-50": typeof onInvest === 'function' && isInvestDisabled,
      }
    );

    const handleClick = () => {
      if (isClickable) {
        onInvest(label);
      }
    };

    return (
      <div className={cardClasses} onClick={handleClick} title={isClickable ? `Investir dans ${label}` : ''}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-slate-300 text-sm">{label}</span>
          </div>
          {typeof onInvest === 'function' && (
            <div
              className={`relative ml-2 p-2 rounded-full transition-all duration-300 transform group overflow-hidden
              ${isSelected
                  ? 'bg-green-500 shadow-green-500/50'
                  : 'bg-gradient-to-r from-yellow-500 to-amber-500 shadow-yellow-500/50'
                }`}
            >
              <HandCoins size={16} className="text-black" />
            </div>
          )}
        </div>
        <div className="flex items-end justify-between">
          <span className="text-white text-lg font-bold">{value}</span>
          {impact !== undefined && impact !== null && Number(impact) !== 0 && (
            <span className={`text-sm font-medium ${impact > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {impact > 0 ? '+' : ''}{impact}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full bg-slate-900 overflow-y-auto custom-scrollbar">
      <div className="p-4 border-b border-slate-700">
        <div className="mb-4">
          <h2 className="text-white font-bold flex items-center gap-2">
            <Crown size={16} className="text-yellow-400" />
            {gameState.joueur.name}
          </h2>
          <div className="text-slate-400 text-sm">{gameState.joueur.leader}</div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <MobileStatCard
            label="Population"
            icon={<Users size={16} color="#8b5cf6" />}
            value={gameState.joueur.population}
            impact={impactsByParam.population?.[0]?.impact}
          />
          <MobileStatCard
            label="Économie"
            icon={<HandCoins size={16} color="#facc15" />}
            value={gameState.joueur.economy}
            impact={impactsByParam.economy?.[0]?.impact}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Économie")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Économie")}
          />
          <MobileStatCard
            label="Militaire"
            icon={<Shield size={16} color="#ef4444" />}
            value={gameState.joueur.military}
            impact={impactsByParam.military?.[0]?.impact}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Militaire")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Militaire")}
          />
          <MobileStatCard
            label="Technologie"
            icon={<Laptop size={16} color="#3b82f6" />}
            value={gameState.joueur.technology}
            impact={impactsByParam.technology?.[0]?.impact}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Technologie")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Technologie")}
          />
          <MobileStatCard
            label="Diplomatie"
            icon={<Handshake size={16} color="#ffffff" />}
            value={gameState.joueur.diplomacy}
            impact={impactsByParam.diplomacy?.[0]?.impact}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Diplomatie")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Diplomatie")}
          />
          <MobileStatCard
            label="Ressources"
            icon={<Leaf size={16} color="#22c55e" />}
            value={gameState.joueur.natural_resources}
            impact={impactsByParam.natural_resources?.[0]?.impact}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Ressources")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Ressources")}
          />
          <MobileStatCard
            label="Stabilité"
            icon={<Scale size={16} color="#000000" />}
            value={gameState.joueur.stability}
            impact={impactsByParam.stability?.[0]?.impact}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Stabilité")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Stabilité")}
          />

          <MobileStatCard
            label="Nucléaire"
            icon={redBtn ? (
              <button
                className="nuclear-button"
                title="Déclencher l'arme nucléaire"
                onClick={(e) => { e.stopPropagation(); nucleaire(); }}
              >
                <svg viewBox="0 0 512 512" width="24" height="24"> <g> <path className="st0" d="M256.04,273.772c6.625,0,13.219-1.766,19.016-5.109c18.156-10.5,24.406-33.828,13.922-52.031 c-6.781-11.75-19.438-19.031-33.016-19.031c-6.656,0-13.219,1.766-19.016,5.109c-18.172,10.516-24.438,33.828-13.922,52.031 C229.805,266.475,242.461,273.772,256.04,273.772z"></path> <path className="st0" d="M296.633,306.084c-3.625-6.297-11.328-8.891-18.031-6.109c0,0,1.031,0.391-4.734,1.969 c-5.766,1.563-11.781,2.375-17.875,2.375c-6.109,0-12.094-0.813-17.875-2.375c-5.766-1.578-4.719-1.969-4.719-1.969 c-6.703-2.781-14.438-0.188-18.031,6.109l-80.156,138.812c-2,3.469-2.484,7.609-1.313,11.438c1.188,3.828,3.891,6.984,7.516,8.75 c0,0,7.219,4.438,18.297,8.891c30.484,12.297,63.234,18.703,96.281,18.766c33.063-0.063,65.781-6.469,96.297-18.766 c11.078-4.453,18.297-8.891,18.297-8.891c3.594-1.766,6.328-4.922,7.5-8.75s0.688-7.969-1.297-11.438L296.633,306.084z"></path> <path className="st0" d="M510.508,199.928c-4.594-32.563-15.422-64.125-31.906-92.781c-16.547-28.609-38.469-53.719-64.375-74.016 c-9.422-7.344-16.875-11.391-16.875-11.391c-3.281-2.25-7.391-3.016-11.328-2.109c-3.906,0.891-7.25,3.375-9.266,6.844 l-80.125,138.813c-3.625,6.266-2.016,14.266,3.734,18.672c0,0-0.188-1.109,4.078,3.109c4.203,4.219,7.938,9.016,11,14.281 c3.031,5.313,5.328,10.906,6.859,16.672c1.531,5.797,0.672,5.078,0.672,5.078c0.938,7.203,7.031,12.594,14.313,12.594H497.57 c4,0,7.828-1.672,10.563-4.609s4.109-6.875,3.828-10.859C511.961,220.225,512.195,211.741,510.508,199.928z"></path> <path className="st0" d="M189.024,223.1c0,0-0.859,0.719,0.672-5.078c1.531-5.766,3.797-11.359,6.859-16.672 c3.063-5.281,6.766-10.063,11-14.281s4.063-3.109,4.063-3.109c5.766-4.406,7.375-12.406,3.75-18.672L135.211,26.475 c-2-3.469-5.359-5.953-9.266-6.844c-3.906-0.906-8.016-0.141-11.328,2.109c0,0-7.453,4.047-16.844,11.391 c-25.906,20.297-47.828,45.406-64.406,74.016c-16.469,28.656-27.281,60.219-31.906,92.781c-1.656,11.813-1.422,20.297-1.422,20.297 c-0.281,3.984,1.063,7.922,3.828,10.859c2.719,2.938,6.547,4.609,10.563,4.609h160.281 C181.977,235.694,188.086,230.303,189.024,223.1z"></path> </g> </svg>
              </button>
            ) : (
              <Radiation size={20} className="inline-icon" color="#00ff1a" />
            )}
            value={
              hasImpactNucl ? (
                <>
                  <span>{nuclearValue - impactNucl}</span>
                  <span className="stat-arrow">→</span>
                  <span style={{ color: impactNucl >= 0 ? "#4caf50" : "#f44336", fontWeight: "bold" }}>
                    {nuclearValue}
                  </span>
                </>
              ) : (
                <span>{nuclearValue}</span>
              )
            }
            impact={null}
            onInvest={handleInvest}
            isSelected={pendingInvestments.includes("Nucléaire")}
            isInvestDisabled={!canPerformAction && !pendingInvestments.includes("Nucléaire")}
          />
        </div>
      </div>
    </div>
  );
};

export default MobileData;